'use client';

import 'keen-slider/keen-slider.min.css';
import { KeenSliderInstance, KeenSliderOptions, useKeenSlider } from 'keen-slider/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { PUBLIC_IS_BOT } from '@/app/_constants/search-engines';
import Arrow from './arrow';
import Dots from './dots';
import ThumbnailCarousel from './thumbnail-carousel';
import { ArrowProps, CarouselOptions, DotsType } from './types';
interface Props {
  children: React.ReactNode;
  thumbnailChildren?: React.ReactNode;
  totalCountChildren?: React.ReactNode;
  className?: string;
  onSlideChange?: (activeIndex: number) => void;
  detailsChanged?: (slider: KeenSliderInstance) => void;
  activeIndex?: number;
  staticChildren?: boolean;
  options?: CarouselOptions;
  thumbnailOptions?: CarouselOptions;
  arrowType?: ArrowProps['type'];
  containerClassName?: string;
  skeleton?: React.ReactNode;
  hideLeftArrowOnLastSlide?: boolean;
  dotsType?: DotsType;
  loadedCallback?: (isLoaded: boolean) => void;
  thumbnailChildrenClassName?: string;
}
const NavigationCarousel = ({
  children,
  thumbnailChildren,
  totalCountChildren,
  className,
  onSlideChange,
  detailsChanged,
  activeIndex,
  staticChildren = false,
  options,
  containerClassName,
  thumbnailOptions,
  arrowType,
  skeleton,
  hideLeftArrowOnLastSlide = false,
  dotsType,
  loadedCallback,
  thumbnailChildrenClassName
}: Props) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(PUBLIC_IS_BOT);
  const [hideLeftArrow, setHideLeftArrow] = useState(false);
  const shouldUpdate = useRef(false);
  const isAnimating = useRef(false);
  const slideChangeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const config: KeenSliderOptions = useMemo(() => ({
    initial: 0,
    slideChanged(slider: KeenSliderInstance) {
      setHideLeftArrow(slider.track.details.abs >= slider.track.details.maxIdx - (hideLeftArrowOnLastSlide ? 1 : 0));
      setCurrentSlide(slider.track.details.rel);
      if (onSlideChange) {
        if (slideChangeTimeoutRef.current) clearTimeout(slideChangeTimeoutRef.current);
        slideChangeTimeoutRef.current = setTimeout(() => onSlideChange(slider.track.details.rel), 200);
      }
    },
    detailsChanged(slider: KeenSliderInstance) {
      detailsChanged?.(slider);
    },
    animationStarted: () => {
      isAnimating.current = true;
    },
    animationEnded(slider: KeenSliderInstance) {
      isAnimating.current = false;
      if (!staticChildren && shouldUpdate.current) {
        setTimeout(() => {
          slider.update();
          shouldUpdate.current = false;
        }, 50);
      }
    },
    created() {
      setLoaded(true);
      loadedCallback && loadedCallback(true);
    },
    mode: options?.mode || 'free',
    slides: {
      perView: options?.perView,
      spacing: options?.spacing ?? 15
    },
    rtl: true,
    ...options
  }), [onSlideChange, detailsChanged, options, hideLeftArrowOnLastSlide, loadedCallback, staticChildren]);
  if (options?.breakPoints) {
    const mobileConfig = options.breakPoints.mobile;
    const desktopConfig = options.breakPoints.desktop;
    config.breakpoints = {
      '(max-width: 1200px)': {
        slides: {
          perView: mobileConfig.perView,
          spacing: mobileConfig.spacing || options.spacing,
          origin: mobileConfig.origin
        },
        drag: mobileConfig.drag === undefined ? true : mobileConfig.drag
      },
      '(min-width: 1201px)': {
        slides: {
          perView: desktopConfig.perView,
          spacing: desktopConfig.spacing || options.spacing,
          origin: desktopConfig.origin
        },
        drag: desktopConfig.drag === undefined ? true : desktopConfig.drag
      }
    };
  }
  const [sliderRef, instanceRef] = useKeenSlider<HTMLElement>(config);
  useEffect(() => {
    return () => {
      if (slideChangeTimeoutRef.current) clearTimeout(slideChangeTimeoutRef.current);
    };
  }, []);
  useEffect(() => {
    if (staticChildren) return;
    if (isAnimating.current) shouldUpdate.current = true;else {
      instanceRef.current?.update(config);
      shouldUpdate.current = false;
    }
  }, [children, config, instanceRef, hideLeftArrowOnLastSlide, staticChildren]);
  useEffect(() => {
    const currIndex = instanceRef.current?.track?.details?.abs || 0;
    const maxIndex = instanceRef.current?.track?.details?.maxIdx || 0;
    setHideLeftArrow(currIndex >= maxIndex - (hideLeftArrowOnLastSlide ? 1 : 0));
  }, [hideLeftArrowOnLastSlide, instanceRef]);
  useEffect(() => {
    if (!detailsChanged || !loaded) return;
    const frame = requestAnimationFrame(() => {
      if (instanceRef.current) detailsChanged(instanceRef.current);
    });
    return () => cancelAnimationFrame(frame);
  }, [detailsChanged, loaded, children, instanceRef]);
  useEffect(() => {
    if (!activeIndex && activeIndex !== 0 || activeIndex === currentSlide || !instanceRef.current) return;
    instanceRef.current.moveToIdx(activeIndex);
  }, [activeIndex]); // eslint-disable-line

  return <>
            <div className={classNames('navigation-wrapper group relative w-full', className, {
      invisible: !loaded && skeleton
    })}>
                <div ref={sliderRef} className={classNames('keen-slider', containerClassName)}>
                    {children}
                </div>
                {!!arrowType && loaded && instanceRef.current && <>
                        <Arrow direction='left' onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          if (instanceRef.current) instanceRef.current.next();
        }} disabled={hideLeftArrow} type={arrowType} />

                        <Arrow direction='right' onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          if (instanceRef.current) instanceRef.current?.prev();
        }} disabled={currentSlide === 0 && !options?.loop} type={arrowType} />
                    </>}
                {!!dotsType && loaded && instanceRef.current && <Dots instanceRef={instanceRef} currentSlide={currentSlide} dotsType={dotsType} />}
            </div>

            {!!totalCountChildren && totalCountChildren}

            {!!thumbnailChildren && <div className={thumbnailChildrenClassName}>
                    <ThumbnailCarousel sliderInstanceRef={instanceRef} thumbnailOptions={thumbnailOptions}>
                        {thumbnailChildren}
                    </ThumbnailCarousel>
                </div>}

            {!loaded && skeleton}
        </>;
};
export default NavigationCarousel;